// ...new file for CMS data fetching logic...

import { PageData } from "./index";
import { getProductsByIds } from "@/store/productSlice";

export const CMS_HOST_URL = 'https://d2ffb7z1ailwvo.cloudfront.net/';

const generateProductCardsData2 = (data: any, vendorName: string) => {
    return data?.products?.map((product: any) => {
        let pdpUrl = getBrandPathEntry(vendorName) + `/product/${product.productId}`;
        let roomTypeParam = product.roomType ? '&roomType=' + product.roomType : '';
        let productTypeParam = product.productType ? '&productType=' + product.productType : '';
        let viewInYourRoomLink = `/builder?productId=${product.productId}&showcase=true&auth=false${roomTypeParam}${productTypeParam}`;
        return {
            name: product.productName,
            id: product.productId,
            image: product.productImage,
            model3dUrl: product.model3dUrl,
            pdpUrl: pdpUrl,
            viewMoreUrl: "",
            price: product.price,
            comparePrice: product.comparePrice,
            viewInYourRoomLink: viewInYourRoomLink,
            otherSizes: product.otherSizes,
            otherColors: product.otherColors,
            priceRange: product.priceRange,
            expiresOn: product.expiresOn ? `Ends : ${new Date(product.expiresOn).toLocaleDateString()}` : 'While supplies last.',
            isBestSeller: product.isBestSeller,
            colorVariants:product?.colorVariants
        };
    });
};

export const getBrandPathEntry = (vendorName: string) => {
    return vendorName && vendorName !== 'nestingale' ? `/${vendorName}` : "";
};
//import jsonObj from "/Users/rajendarmunjam/workspace/cms/stage/category/sofas.json";
//import jsonObj from "/Users/rajendarmunjam/workspace/cms/stage/category/supplier-assistant.json";

// ─── Local JSON testing ──────────────────────────────────────────────
// Uncomment ONE path below to load from local cms folder during dev.
// Keep all lines commented before committing.
const LOCAL_JSON_PATH: string = ''
//const LOCAL_JSON_PATH: string = '/Users/rajendarmunjam/workspace/cms/stage/home.json'
//const LOCAL_JSON_PATH: string = '/Users/rajendarmunjam/workspace/cms/stage/product-supplier.json'
//const LOCAL_JSON_PATH: string = '/Users/rajendarmunjam/workspace/cms/stage/realtors/a.json'
// ─────────────────────────────────────────────────────────────────────

export const getCmsData = async (vendorName: string, cmsRelUrl: string, env: string) => {
    let cmsData: PageData = {
        sections: {},
        title: "",
        description: "",
        order: [],
    };
    try {
        if (LOCAL_JSON_PATH) {
            // Local dev override — read from filesystem instead of CDN
            const fs = await import('fs');
            const raw = fs.default.readFileSync(LOCAL_JSON_PATH, 'utf-8');
            console.log(`[CMS] Loaded local: ${LOCAL_JSON_PATH}`);
            cmsData = JSON.parse(raw);
        } else {
            let cmsDataPromise = await fetch(`${CMS_HOST_URL}${cmsRelUrl}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            cmsData = await cmsDataPromise.json();
        }
        //cmsData = jsonObj as any; 

        const preprocessors = {
            getProductDetails: async (section: any) => {
                const inputSource = section.preprocess?.input || section.preProcess?.input;
                if (inputSource && inputSource.length > 0) {
                    let productsDataPromise = await getProductsByIds({ productIds: inputSource, env: env, brand: vendorName });
                    let data = await productsDataPromise;
                    if (data && data.products && data.products.length > 0) {
                        const processedData = generateProductCardsData2(data, vendorName);
                        section.items = processedData;
                    }
                }
            }
        };

        const processSections = async () => {
            try {
                const sectionPromises = Object.values(cmsData.sections || {}).map(async (section: any) => {
                    try {
                        const currentPreprocess = section.preprocess || section.preProcess;
                        if (currentPreprocess && currentPreprocess.method) {
                            if (currentPreprocess.method in preprocessors) {
                                await preprocessors[currentPreprocess.method as keyof typeof preprocessors](section);
                            } else {
                                console.error(`Invalid preprocess method: ${currentPreprocess.method}`);
                            }
                        }
                    } catch (sectionError) {
                        console.error(`Error processing section ${section.name || section.trackId}:`, sectionError);
                    }
                    if (section.include && typeof section.include === 'string') {
                        try {
                            const baseCmsPath = `${CMS_HOST_URL}${env === 'qa' ? 'stage' : 'prod'}/`;
                            const includeUrl = new URL(section.include, baseCmsPath).toString();
                            console.log(`Including shared section from: ${includeUrl}`);
                            const includeDataPromise = await fetch(includeUrl, {
                                method: 'GET',
                                headers: {
                                    'Content-Type': 'application/json'
                                }
                            });
                            if (!includeDataPromise.ok) {
                                throw new Error(`Failed to fetch included JSON: ${includeDataPromise.statusText} from ${includeUrl}`);
                            }
                            const includeData = await includeDataPromise.json();
                            const originalUiCompType = section.uiCompType;
                            Object.assign(section, includeData);
                            if (originalUiCompType && Object.keys(originalUiCompType).length > 0) {
                                section.uiCompType = { ...includeData.uiCompType, ...originalUiCompType };
                            }
                            delete section.include;
                        } catch (includeError) {
                            console.error(`Error including data for section ${section.name || section.trackId} from ${section.include}:`, includeError);
                        }
                    }
                });

                await Promise.all(sectionPromises);
                console.log('All sections processed');
                return cmsData;
            } catch (error) {
                console.error('Error processing sections:', error);
                return null;
            }
        };

        await processSections().then((result) => {
            if (result) {
                console.log('Processing completed successfully');
            } else {
                console.log('Processing failed');
            }
        }).catch((error) => {
            console.error('Unexpected error:', error);
        });
        return cmsData;

    } catch (e) {
        console.error("Error in fetching cms data", e);
    }
};

/**
 * Fetches and returns the header config object from a named header JSON file.
 * In local dev (when LOCAL_JSON_PATH is set) it reads from the local cms/stage/header/ directory.
 * In production it fetches from the CDN.
 */
export const getHeaderConfig = async (headerJsonName: string, env: string): Promise<any | null> => {
    try {
        if (LOCAL_JSON_PATH) {
            // Derive the cms stage base dir from LOCAL_JSON_PATH
            // e.g.  /Users/.../cms/stage/realtors/b.json  →  /Users/.../cms/stage
            const stageIdx = LOCAL_JSON_PATH.indexOf('/stage/');
            const localBase = stageIdx >= 0
                ? LOCAL_JSON_PATH.substring(0, stageIdx + '/stage'.length)
                : LOCAL_JSON_PATH.substring(0, LOCAL_JSON_PATH.lastIndexOf('/'));
            const localHeaderPath = `${localBase}/header/${headerJsonName}`;
            const fs = await import('fs');
            const raw = fs.default.readFileSync(localHeaderPath, 'utf-8');
            console.log(`[CMS] Loaded local header JSON: ${localHeaderPath}`);
            return JSON.parse(raw);
        } else {
            const basePath = env === 'qa' ? 'stage' : 'prod';
            const headerRelUrl = `${basePath}/header/${headerJsonName}`;
            const resp = await fetch(`${CMS_HOST_URL}${headerRelUrl}`);
            if (!resp.ok) throw new Error(`Failed to fetch header JSON: ${resp.statusText}`);
            return await resp.json();
        }
    } catch (e) {
        console.error('[CMS] Failed to load headerJson:', headerJsonName, e);
        return null;
    }
};
